// viewModels/LocationScreenViewModel.ts
import { Analytics } from '@core/Analytics/Analytics';
import { Application } from '@core/Application';
import { DemoServices } from '@services/DemoServices';
import { Logger } from '@utils/Logger';
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

export const useLocationScreenViewModel = () => {
    const navigate = useNavigate();
    
    const [locationName, setLocationName] = useState('');
    const [locationNameError, setLocationNameError] = useState<string | null>(null);
    
    const [address, setAddress] = useState('');
    const [addressError, setAddressError] = useState<string | null>(null);
    
    const [userLocation, setUserLocation] = useState({ 
        lat: -34.6037, 
        lng: -58.3816 
    }); // Buenos Aires default
    
    const [map, setMap] = useState<google.maps.Map | null>(null);
    const [marker, setMarker] = useState<google.maps.Marker | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [serviceError, setServiceError] = useState<string | null>(null);
    
    const mapRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        loadGoogleMapsScript();
        
        return () => {
            // Cleanup if needed
        };
    }, []);

    const analytics = new AnalyticsHelper()

    const viewDidAppear = () => {
        analytics.trackScreenShown()
    }

    const loadGoogleMapsScript = () => {
        if (window.google) {
            initMap();
            return;
        }

        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=places`;
        script.async = true;
        script.onload = () => initMap();
        document.head.appendChild(script);
    };

    const initMap = () => {
        if (!window.google || !mapRef.current) return;

        const mapInstance = new window.google.maps.Map(mapRef.current, {
            center: userLocation,
            zoom: 15,
            disableDefaultUI: true,
            zoomControl: true,
        });

        const markerInstance = new window.google.maps.Marker({
            position: userLocation,
            map: mapInstance,
            draggable: true,
        });

        markerInstance.addListener('dragend', (e: google.maps.MapMouseEvent) => {
            if (e.latLng) {
                const newPos = { lat: e.latLng.lat(), lng: e.latLng.lng() };
                setUserLocation(newPos);
                reverseGeocode(newPos);
            }
        });

        // Update location when map is clicked
        mapInstance.addListener('click', (e: google.maps.MapMouseEvent) => {
            if (e.latLng) {
                const newPos = { lat: e.latLng.lat(), lng: e.latLng.lng() };
                setUserLocation(newPos);
                markerInstance.setPosition(newPos);
                reverseGeocode(newPos);
            }
        });

        setMap(mapInstance);
        setMarker(markerInstance);

        // Get user's current location
        getCurrentLocation(mapInstance, markerInstance);
    };

    const getCurrentLocation = (
        mapInstance: google.maps.Map, 
        markerInstance: google.maps.Marker
    ) => {
        if (navigator.geolocation) {
            setIsLoading(true);
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const pos = {
                        lat: position.coords.latitude,
                        lng: position.coords.longitude,
                    };
                    setUserLocation(pos);
                    mapInstance.setCenter(pos);
                    markerInstance.setPosition(pos);
                    reverseGeocode(pos);
                    setIsLoading(false);
                },
                (error) => {
                    console.error('Error getting location:', error);
                    setIsLoading(false);
                }
            );
        }
    };

    const reverseGeocode = (location: { lat: number; lng: number }) => {
        if (!window.google) return;

        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ location }, (results, status) => {
            if (status === 'OK' && results && results[0]) {
                setAddress(results[0].formatted_address);
            }
        });
    };

    const handleAddressChange = (value: string) => {
        setAddress(value);
        setAddressError(null);

        // Geocode the address with debouncing would be better in production
        if (value && window.google) {
            const geocoder = new window.google.maps.Geocoder();
            geocoder.geocode({ address: value }, (results, status) => {
                if (status === 'OK' && results && results[0]) {
                    const location = results[0].geometry.location;
                    const pos = { lat: location.lat(), lng: location.lng() };
                    setUserLocation(pos);
                    if (map) map.setCenter(pos);
                    if (marker) marker.setPosition(pos);
                }
            });
        }
    };

    const validateLocationName = () => {
        if (!locationName.trim()) {
            setLocationNameError("El nombre del lugar es requerido".localized());
            return false;
        }
        setLocationNameError(null);
        return true;
    };

    const validateAddress = () => {
        if (!address.trim()) {
            setAddressError("La dirección es requerida".localized());
            return false;
        }
        setAddressError(null);
        return true;
    };

    const validateAll = () => {
        const isLocationNameValid = validateLocationName();
        const isAddressValid = validateAddress();
        return isLocationNameValid && isAddressValid;
    };

    const handleSubmit = async () => {
        if (!validateAll()) {
            return false;
        }

        if (isLoading) { 
            Logger.log("handleSubmit", "called twice")
            return false 
        }

        let locationData = {
            qpID: undefined,
            locationName,
            address,
            coordinates: {
                latitude: userLocation.lat,
                longitude: userLocation.lng,
            }
        };
        
        console.log('Location submitted:', locationData);
        setIsCreating(true)
        let service = new DemoServices()

        try {

            setServiceError(undefined)
            let qpID = await service.createQontactPoint(
                locationName,
                userLocation.lat,
                userLocation.lng
            )
            locationData.qpID = qpID
            setIsCreating(false)

        } catch (error) {
            Logger.logError("LocationScreenViewModel", error)
            setServiceError("Hubo un error al intentar crear el Qontact Point".localized())
            setIsCreating(false)
            return false
        }
        // Navigate to next screen with location data
        navigate('/scan-qr-code', { 
            state: locationData
        });
        
        return true;
    };

    return {
        // State
        locationName,
        locationNameError,
        address,
        addressError,
        userLocation,
        isLoading,
        isCreating,
        mapRef,
        serviceError,
        
        // Setters
        setLocationName,
        setAddress: handleAddressChange,
        
        // Actions
        handleSubmit,
        validateLocationName,
        validateAddress,
        viewDidAppear,
    };
};

class AnalyticsHelper {
    private analytics: Analytics
    constructor(analytics: Analytics = Application.shared().coreDependencies.analytics) {
        this.analytics = analytics
    }

    async trackScreenShown() {
        this.analytics.track({
            eventType: 'AutoDemo.LocationScreen.Shown',
        })
    }

    async trackFormCompleted() {
        this.analytics.track({
            eventType: 'AutoDemo.LocationScreen.formCompleted',
        })
    }
}